import { useAnalytics } from '@/contexts/analytics-context/use-analytics';
import { isOpenBankingAccount } from '@/utils/accounts/is-open-banking-account';
import { FinancialServiceProviderAdapters } from '@seapoint/server/enums';
import { AxiosError } from 'axios';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useErrorBoundary } from 'react-error-boundary';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import useSWRMutation from 'swr/mutation';
import { useAccountAPI } from '../../../../api/use-account-api';
import { useData } from '../../../../contexts/data-context/use-data';
import { useUserInfo } from '../../../../contexts/user-context/use-user-info';
import { LoadingSection } from '../../../../design-system/loading/loading';
import { AccountView } from '../../../../types/entity-types';
import { isBankingAccount } from '../../../../utils/accounts/is-banking-account';
import { handleException } from '../../../../utils/handle-exception';
import { postSwrFetcherWithDynamicUrl } from '../../../../utils/post-swr-fetcher-with-dynamic-url';
import { ConnectedAccountsPage } from './connected-accounts-page';
import { useReAuthLink } from './utils';

const SUPPORTED_FSP_ADAPTERS_FOR_SEAPOINT_ACCOUNTS = [
  FinancialServiceProviderAdapters.MODULR,
  FinancialServiceProviderAdapters.WEALTHKERNEL,
];

type UnlinkAccountArg = {
  urlSuffix: string;
};

export const ConnectedAccountsPageWrapper = () => {
  const { syncAccounts, isSyncing: isSyncingAccounts } = useAccountAPI();
  const navigate = useNavigate();
  const { data, isLoading, error, mutations } = useData();
  const { showBoundary } = useErrorBoundary();
  const { gmailConnectorPoc, isDemoAccountsEnabled } = useFlags();
  const { authenticatedInfo } = useUserInfo();
  const analytics = useAnalytics();

  // Handle OAuth error params from redirect (e.g., duplicate connection)
  const urlParams = new URLSearchParams(window.location.search);
  const urlError = urlParams.get('error');
  const urlErrorMessage = urlParams.get('message');
  if (urlError) {
    toast.error(urlErrorMessage || 'Failed to connect account');
    window.history.replaceState({}, '', window.location.pathname);
  }

  const { trigger: triggerUnlinkAccount } = useSWRMutation<void, AxiosError, string, UnlinkAccountArg>(
    '/api/accounts/',
    postSwrFetcherWithDynamicUrl,
  );

  const { trigger: triggerReAuthLink } = useReAuthLink();

  if (isLoading) {
    return <LoadingSection text="Loading accounts..." />;
  }

  if (!data) {
    showBoundary(error);

    return null;
  }

  const { accounts } = data;

  const onSyncAccounts = () => {
    // Using data from first company because orgs can only have one company for now
    syncAccounts(accounts[0].company.id);
  };

  if (accounts.length === 0) {
    navigate('../open-seapoint-account');

    return null;
  }

  const onDeleteAccount = async (accountId: string) => {
    try {
      await triggerUnlinkAccount({ urlSuffix: `${accountId}/unlink` });

      await mutations.handleDeleteUpdates({
        entityType: 'account',
        entityIds: [accountId],
      });

      toast.success('The account has been disconnected');
    } catch (error) {
      handleException({ error, toastMessage: 'Failed to disconnect the account' });
    }
  };

  const onReconnectAccount = async (account: AccountView) => {
    if (!account.institution?.externalId) {
      toast.error('Cannot reconnect: missing institution information');
      return;
    }

    // Stripe accounts use OAuth flow, not Yapily re-authorization
    if (account.fspAdapter === FinancialServiceProviderAdapters.STRIPE) {
      toast.info('Stripe reconnection: please disconnect and reconnect the account');
      return;
    }

    try {
      const authLinkResult = await triggerReAuthLink({
        companyId: account.company.id,
        institutionId: account.institution.externalId,
        accountId: account.id,
        userName: authenticatedInfo!.name,
      });

      if (authLinkResult?.link) {
        // Store institution info for success page
        localStorage.setItem(
          'selectedInstitution',
          JSON.stringify({
            name: account.institution?.name,
            logo: account.institution?.logo,
          }),
        );

        // Redirect to bank OAuth
        window.location.href = authLinkResult.link;
      }
    } catch (error) {
      console.error('Reconnection error:', error);
      handleException({ error, toastMessage: 'Failed to initiate reconnection' });
    }
  };

  const seapointAccounts = accounts.filter(
    (account) => account.fspAdapter && SUPPORTED_FSP_ADAPTERS_FOR_SEAPOINT_ACCOUNTS.includes(account.fspAdapter),
  );

  const bankingAccounts = accounts.filter(isBankingAccount);

  const openBankingAccounts = bankingAccounts.filter(isOpenBankingAccount);
  const demoAccounts = bankingAccounts.filter(
    (account) => account.fspAdapter === FinancialServiceProviderAdapters.GOCARDLESS_DEMO_DATA,
  );

  // Determine which demo variants already exist
  const hasDemoEUR = demoAccounts.some((account) => account.currency === 'EUR');
  const hasDemoGBP = demoAccounts.some((account) => account.currency === 'GBP');

  return (
    <ConnectedAccountsPage
      shouldShowConnectDemoDataEURButton={isDemoAccountsEnabled && !hasDemoEUR}
      shouldShowConnectDemoDataGBPButton={isDemoAccountsEnabled && !hasDemoGBP}
      shouldShowGmailConnectorButton={gmailConnectorPoc}
      openBankingAccounts={openBankingAccounts}
      seapointAccounts={seapointAccounts}
      demoAccounts={demoAccounts}
      onGenerateDemoDataEUR={() => {
        // EUR via Revolut
        const demoInstitutionData = {
          name: 'Revolut',
          logo: 'https://storage.googleapis.com/gc-prd-institution_icons-production/UK/PNG/revolut.png',
        };
        localStorage.setItem('selectedInstitution', JSON.stringify(demoInstitutionData));
        window.location.href = `/api/gocardless/auth/${accounts[0].company.id}?ref=demo&demo=true&demoVariant=eur_revolut`;

        analytics.track('demo_data_generated', {
          currency: 'EUR',
          source_page: 'connected_accounts_page',
        });
      }}
      onGenerateDemoDataGBP={() => {
        // GBP via HSBC
        const demoInstitutionData = {
          name: 'HSBC UK',
          logo: 'https://storage.googleapis.com/gc-prd-institution_icons-production/UK/PNG/hsbc.png',
        };
        localStorage.setItem('selectedInstitution', JSON.stringify(demoInstitutionData));
        window.location.href = `/api/gocardless/auth/${accounts[0].company.id}?ref=demo&demo=true&demoVariant=gbp_hsbc`;

        analytics.track('demo_data_generated', {
          currency: 'GBP',
          source_page: 'connected_accounts_page',
        });
      }}
      isSyncingAccounts={isSyncingAccounts}
      onSyncAccounts={onSyncAccounts}
      onLinkAccount={() => {
        navigate('../connect-open-banking');

        analytics.track('link_bank_account_clicked', {
          source_page: 'accounts',
        });
      }}
      onLinkEmailAccount={() => {
        navigate('../connect-email');
      }}
      onDeleteAccount={onDeleteAccount}
      onReconnectAccount={onReconnectAccount}
    />
  );
};
